import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Safe localStorage wrapper for WebViews that block it
const safeStorage = {
  getItem: (key) => { try { return localStorage.getItem(key); } catch (e) { return null; } },
  setItem: (key, value) => { try { localStorage.setItem(key, value); } catch (e) { /* WebView restriction */ } },
  removeItem: (key) => { try { localStorage.removeItem(key); } catch (e) { /* WebView restriction */ } }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(safeStorage.getItem('token'));

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  const fetchUser = useCallback(async () => {
    if (!token) return;
    try {
      api.defaults.headers.Authorization = `Bearer ${token}`;
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      api.defaults.headers.Authorization = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Refresh user data when window gets focus (to catch admin changes)
  useEffect(() => {
    const handleFocus = () => {
      if (token) {
        fetchUser();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [token, fetchUser]);

  const login = async (login, password) => {
    const response = await api.post('/auth/login', { login, password });
    const { access_token, user: userData } = response.data;
    safeStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    // Update api headers immediately for subsequent calls
    api.defaults.headers.Authorization = `Bearer ${access_token}`;
    return userData;
  };

  const register = async (email, password, pseudo) => {
    const response = await api.post('/auth/register', { email, password, pseudo });
    const { access_token, user: userData } = response.data;
    safeStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    // Update api headers immediately for subsequent calls
    api.defaults.headers.Authorization = `Bearer ${access_token}`;
    return userData;
  };

  const logout = () => {
    safeStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser, api }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
